import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Text from 'components/Text';
import { PrimaryButton } from 'components';
import { mobile, tablet } from 'styles/breakpoints';
import quizSvgs from 'utils/constants/quizSvgs';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';
import Line from 'assets/icons/line.svg';
import Checked from 'assets/icons/checkmark.svg';
import { CustomScroll } from 'react-custom-scroll';

const arrow = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12.3151 15.8685L18.3686 9.76671C18.6493 9.48377 18.4505 9 18.0535 9H5.94647C5.5495 9 5.3507 9.48377 5.6314 9.76671L11.6849 15.8685C11.8589 16.0438 12.1411 16.0438 12.3151 15.8685Z" fill="#454545"/>
</svg>`;

interface InputProp {
  title?: string;
  subtitle?: string;
  backgroundSvg?: string;
  hasSkipButton?: string;
  skipButtonText?: string;
  onContinue: (value: { hours: string; minutes: string; ampm: string }) => void;
  onSkip?: () => void;
}

const WrapperButtons = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  width: 100%;
`;

const Button = styled(PrimaryButton)`
  width: 100%;
  max-width: 28.75rem;
  margin: 0 auto;
`;

const Title = styled(Text)`
  color: #000;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2.0625rem;
  margin-bottom: 0.5rem;
  text-align: center;
  @media ${tablet} {
    font-size: 1.375rem;
    line-height: 2rem;
    font-weight: 700;
  }
`;

const Subtitle = styled(Text)`
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  @media ${tablet} {
    font-size: 0.8125rem;
    line-height: normal;
    margin-bottom: 1rem;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3.5rem 1rem;
  max-width: 28.75rem;
  width: 100%;
  @media ${tablet} {
    padding-top: 1.5rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 28.75rem;
  width: 100%;
  position: relative;
`;

const SelectContainer = styled.div<{ isSelected: boolean }>`
  width: 100%;
  height: 3.5rem;
  border: 1px solid #afafaf;
  border-radius: 6.25rem;
  display: flex;
  align-items: center;
  padding: 0 1.25rem;
  background: #fff url('data:image/svg+xml;base64,${btoa(arrow)}') no-repeat
    right 10px center;
  background-size: 1.5rem;
  cursor: pointer;
  color: ${({ isSelected }) => (isSelected ? '#000' : '#AFAFAF')};
  font-size: 1rem;
  font-weight: 500;
`;

const SelectOptions = styled.div`
  top: -100px;
  position: absolute;
  right: 50%;
  transform: translateX(50%);
  width: 100%;
  max-width: 122px;
  border-radius: 12px;
  height: 33rem;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  .rcs-inner-handle {
    background-color: #afafaf;
    width: 3px;
    border-radius: 10px;
  }
  @media ${tablet} {
    max-width: 110px;
  }
`;

const SelectAMPM = styled(SelectOptions)`
  height: 88px;
  top: 8px;
`;

const SelectOption = styled.div<{ isSelected: boolean }>`
  color: #000;
  font-size: 16px !important;
  line-height: 1.18319rem;
  height: 44px;
  font-style: normal;
  font-weight: 400;
  background: rgb(255, 255, 255);
  padding: 0.23rem 1.97rem 0.23rem 1.51rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  &:hover {
    background: #f0f0f0;
  }
  border-bottom: 1px solid #eeecec;
  :nth-last-child(1) {
    border-bottom: none;
  }
  @media ${tablet} {
    font-size: 0.875rem;
    line-height: 1.0625rem;
  }
`;

const BackgroundSvg = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  width: 100%;
  max-width: 48.125rem;
  z-index: -1;
  @media ${tablet} {
    max-width: 28.1875rem;
    max-height: 21.125rem;
    svg {
      min-width: 28.1875rem;
    }
  }
`;

const StyledChecked = styled(Checked)<{ isSelected: boolean }>`
  position: absolute;
  left: 0.43rem;
  width: 0.9rem;
  height: 0.9rem;
  visibility: ${({ isSelected }) => (isSelected ? 'visible' : 'hidden')};

  @media ${tablet} {
    left: 0.43rem;
    width: 0.7rem;
    height: 0.7rem;
  }
`;

const SkipButton = styled.button`
  display: flex;
  max-width: 28.75rem;
  width: 100%;
  height: 3.5rem;
  padding: 0.75rem 1.5rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 6.25rem;
  border: 1px solid rgba(0, 0, 0, 0.6);
  background: #f3f3f8;
  color: rgba(0, 0, 0, 0.7);
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  margin: 0.5rem auto 0;
`;

const Dropdowns = styled.div`
  position: relative;
  display: flex;
  gap: 0.94rem;
  padding: 0 1.063rem;
  @media ${tablet} {
    gap: 0.5rem;
    padding: 0;
  }
`;

const generateHours = (): string[] =>
  Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

const generateMinutes = (): string[] =>
  Array.from({ length: 60 }, (_, i) => (i < 10 ? `0${i}` : i.toString()));

const ampmOptions = ['AM', 'PM'];

const TimePicker: FC<InputProp> = ({
  title,
  subtitle,
  backgroundSvg,
  hasSkipButton,
  skipButtonText,
  onContinue,
  onSkip,
}) => {
  const quizAnswers = useSelector((state: AppState) => state.user.quiz_answers);
  const [selectedHour, setSelectedHour] = useState<string>('12');
  const [selectedMinute, setSelectedMinute] = useState<string>('00');
  const [selectedAmPm, setSelectedAmPm] = useState<string>('PM');

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const ampmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (quizAnswers?.birth_time) {
      setSelectedHour(quizAnswers?.birth_time.hours.padStart(2, '0'));
      setSelectedMinute(quizAnswers?.birth_time.minutes.padStart(2, '0'));
      setSelectedAmPm(quizAnswers?.birth_time.ampm);
    }
  }, [quizAnswers]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        hourRef.current &&
        !hourRef.current.contains(event.target as Node) &&
        minuteRef.current &&
        !minuteRef.current.contains(event.target as Node) &&
        ampmRef.current &&
        !ampmRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const constructTime = () => {
    if (
      selectedHour !== null &&
      selectedMinute !== null &&
      selectedAmPm !== null
    ) {
      onContinue({
        hours: selectedHour,
        minutes: selectedMinute,
        ampm: selectedAmPm,
      });
    }
  };

  const renderSvgImage = (svgKey: string) => {
    const Svg = quizSvgs[svgKey];
    return (
      <BackgroundSvg>
        <Svg />
      </BackgroundSvg>
    );
  };

  const handleSelectClick = (dropdownType: string) => {
    setOpenDropdown(openDropdown === dropdownType ? null : dropdownType);
  };

  const renderOptions = (
    options: string[],
    selectedValue: string,
    setSelectedValue: (value: string) => void,
  ) =>
    options.map(option => (
      <SelectOption
        key={option}
        onClick={() => {
          setSelectedValue(option);
          setOpenDropdown(null);
        }}
        isSelected={selectedValue === option}
      >
        {selectedValue === option && (
          <StyledChecked isSelected={selectedValue === option} />
        )}
        {option}
      </SelectOption>
    ));

  return (
    <Container>
      <Wrapper>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <Dropdowns>
          <SelectContainer
            ref={hourRef}
            isSelected={selectedHour !== null}
            onClick={() => handleSelectClick('hour')}
          >
            {selectedHour !== null ? selectedHour : 'Hour'}
            {openDropdown === 'hour' && (
              <SelectOptions>
                <CustomScroll heightRelativeToParent="100%">
                  {renderOptions(
                    generateHours(),
                    selectedHour,
                    setSelectedHour,
                  )}
                </CustomScroll>
              </SelectOptions>
            )}
          </SelectContainer>

          <SelectContainer
            ref={minuteRef}
            isSelected={selectedMinute !== null}
            onClick={() => handleSelectClick('minute')}
          >
            {selectedMinute !== null ? selectedMinute : 'Minute'}
            {openDropdown === 'minute' && (
              <SelectOptions>
                <CustomScroll heightRelativeToParent="100%">
                  {renderOptions(
                    generateMinutes(),
                    selectedMinute,
                    setSelectedMinute,
                  )}
                </CustomScroll>
              </SelectOptions>
            )}
          </SelectContainer>

          <SelectContainer
            ref={ampmRef}
            isSelected={selectedAmPm !== null}
            onClick={() => handleSelectClick('ampm')}
          >
            {selectedAmPm !== null ? selectedAmPm : 'PM'}
            {openDropdown === 'ampm' && (
              <SelectAMPM>
                {renderOptions(ampmOptions, selectedAmPm, setSelectedAmPm)}
              </SelectAMPM>
            )}
          </SelectContainer>
        </Dropdowns>
      </Wrapper>
      {renderSvgImage(backgroundSvg)}
      <WrapperButtons>
        <Button
          onClick={constructTime}
          disabled={!selectedHour || !selectedMinute || !selectedAmPm}
        >
          Continue
        </Button>
        {hasSkipButton && (
          <SkipButton
            onClick={() =>
              onContinue({
                hours: '12',
                minutes: '00',
                ampm: 'PM',
              })
            }
          >
            {skipButtonText}
          </SkipButton>
        )}
      </WrapperButtons>
    </Container>
  );
};

export default TimePicker;
